import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { api } from "../../lib/api";
import PaymentTermSelect from "./PaymentTermSelect";

const STATUS_BADGE = {
  active: "bg-green-100 text-green-700",
  grace: "bg-yellow-100 text-yellow-700",
  blocked: "bg-red-100 text-red-700",
  disabled: "bg-gray-100 text-gray-600",
};

const INVOICE_PAGE_SIZE = 10;

const INVOICE_BADGE = {
  unpaid: "bg-red-100 text-red-700",
  partial: "bg-yellow-100 text-yellow-700",
  paid: "bg-green-100 text-green-700",
};

// Must match OFFLINE_PAYMENT_METHODS in creditLine.admin.controller.js.
const OFFLINE_PAYMENT_METHODS = ["Bank Transfer", "UPI", "Cash", "Cheque", "Other"];

const todayIst = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const inr = (n) => `INR ${Number(n || 0).toLocaleString("en-IN")}`;

const outstandingOf = (inv) => Math.max(Number(inv.amount) - Number(inv.paid_amount), 0);

export default function CreditLineView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [creditLine, setCreditLine] = useState(null);
  const [pendingTermUpdate, setPendingTermUpdate] = useState(null);
  const [availableBalance, setAvailableBalance] = useState(null);
  const [overdueCount, setOverdueCount] = useState(0);
  const [invoices, setInvoices] = useState([]);
  const [invoiceMeta, setInvoiceMeta] = useState({ total: 0, page: 1, limit: INVOICE_PAGE_SIZE, totalPages: 1 });
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  const [newLimit, setNewLimit] = useState("");
  const [newTerm, setNewTerm] = useState("");
  const [requesting, setRequesting] = useState(false);

  const [payingInvoice, setPayingInvoice] = useState(null);
  const [payMethod, setPayMethod] = useState(OFFLINE_PAYMENT_METHODS[0]);
  const [payReference, setPayReference] = useState("");
  const [payDate, setPayDate] = useState(todayIst());
  const [payNotify, setPayNotify] = useState(true);
  const [payRemarks, setPayRemarks] = useState("");
  const [markingPaid, setMarkingPaid] = useState(false);

  const openMarkPaid = (inv) => {
    setPayingInvoice(inv);
    setPayMethod(OFFLINE_PAYMENT_METHODS[0]);
    setPayReference("");
    setPayDate(todayIst());
    setPayNotify(true);
    setPayRemarks("");
  };

  const closeMarkPaid = () => {
    if (markingPaid) return;
    setPayingInvoice(null);
  };

  const submitMarkPaid = async (e) => {
    e.preventDefault();
    const statusBefore = creditLine.status;
    setMarkingPaid(true);
    try {
      const res = await api.post(`/admin/credit-invoices/${payingInvoice.id}/mark-paid`, {
        payment_method: payMethod,
        payment_reference: payReference.trim() || undefined,
        payment_date: payDate,
        notify_customer: payNotify,
        remarks: payRemarks.trim() || undefined,
        // A customer payment is still processing: the admin explicitly overrides it.
        ...(payingInvoice.payment_state === "processing" ? { override_processing: true } : {}),
      });
      const statusAfter = res.data?.data?.creditLine?.status;
      toast.success(
        statusAfter === "active" && statusBefore !== "active"
          ? "Invoice marked as paid. Credit line is active again."
          : "Invoice marked as paid."
      );
      setPayingInvoice(null);
      load(invoiceMeta.page);
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to mark invoice as paid");
    } finally {
      setMarkingPaid(false);
    }
  };

  const fetchInvoices = (page) =>
    api.get(`/admin/credit-lines/${id}/invoices`, { params: { page, limit: INVOICE_PAGE_SIZE } });

  const applyInvoices = (res, page) => {
    setInvoices(res.data?.data || []);
    setInvoiceMeta(res.data?.meta || { total: 0, page, limit: INVOICE_PAGE_SIZE, totalPages: 1 });
  };

  const load = async (invoicePage = 1) => {
    setLoading(true);
    try {
      const [detailRes, invoicesRes] = await Promise.all([
        api.get(`/admin/credit-lines/${id}`),
        fetchInvoices(invoicePage),
      ]);
      setCreditLine(detailRes.data?.data?.creditLine || null);
      setPendingTermUpdate(detailRes.data?.data?.pendingTermUpdate || null);
      setAvailableBalance(detailRes.data?.data?.available_balance ?? null);
      setOverdueCount(detailRes.data?.data?.overdue_count ?? 0);
      applyInvoices(invoicesRes, invoicePage);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to load credit line");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const handleInvoicePage = async (page) => {
    if (page < 1 || page > invoiceMeta.totalPages) return;
    setInvoicesLoading(true);
    try {
      applyInvoices(await fetchInvoices(page), page);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to load invoices");
    } finally {
      setInvoicesLoading(false);
    }
  };

  const submitTermUpdate = async (e) => {
    e.preventDefault();
    if (!newLimit || !newTerm) {
      toast.error("Enter both new credit limit and new payment term.");
      return;
    }
    setRequesting(true);
    try {
      const res = await api.put(`/admin/credit-lines/${id}`, {
        new_credit_limit: Number(newLimit),
        new_payment_term_days: Number(newTerm),
      });
      toast.success(
        res.status === 201
          ? "Increase sent to customer for acceptance."
          : "Change applied. Customer has been sent an intimation."
      );
      setNewLimit("");
      setNewTerm("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to request term update");
    } finally {
      setRequesting(false);
    }
  };

  const toggleStatus = async () => {
    const action = creditLine.status === "disabled" ? "enable" : "disable";
    try {
      await api.patch(`/admin/credit-lines/${id}/${action}`);
      toast.success(`Credit line ${action}d.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || `Failed to ${action} credit line`);
    }
  };

  const BackLink = () => (
    <button
      type="button"
      onClick={() => navigate("/admin/credit-lines")}
      className="text-sm text-gray-600 hover:text-gray-900 font-semibold flex items-center gap-1 mb-4"
    >
      ← Back to Credit Lines
    </button>
  );

  if (loading) return <div className="p-6 text-gray-500"><BackLink /> Loading…</div>;
  if (!creditLine) return <div className="p-6 text-gray-500"><BackLink /> Credit line not found.</div>;

  return (
    <div className="p-4 sm:p-6 w-full space-y-6">
      <BackLink />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800">
            {creditLine.customer ? `${creditLine.customer.first_name || ""} ${creditLine.customer.last_name || ""}`.trim() : creditLine.user_id}
          </h1>
          <p className="text-sm text-gray-500">{creditLine.customer?.email}</p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize self-start ${STATUS_BADGE[creditLine.status] || ""}`}>
          {creditLine.status}
        </span>
      </div>

      <div className="bg-white rounded-xl border p-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <p className="text-xs text-gray-500">Credit Limit</p>
          <p className="text-lg font-semibold">INR {Number(creditLine.credit_limit).toLocaleString("en-IN")}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Available Balance</p>
          <p className="text-lg font-semibold">
            {availableBalance == null ? "—" : `INR ${Number(availableBalance).toLocaleString("en-IN")}`}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Payment Term</p>
          <p className="text-lg font-semibold">{creditLine.payment_term_days} days</p>
        </div>
      </div>

      {(creditLine.status === "blocked" || creditLine.status === "grace") && (
        <div
          className={`rounded-xl border p-4 text-sm ${
            creditLine.status === "blocked" ? "bg-red-50 border-red-200 text-red-800" : "bg-yellow-50 border-yellow-200 text-yellow-800"
          }`}
        >
          <p className="font-semibold mb-1">
            {creditLine.status === "blocked" ? "Credit line is blocked" : "Credit line is in its grace period"}
          </p>
          <p>
            {overdueCount > 0 ? `${overdueCount} invoice${overdueCount > 1 ? "s are" : " is"} overdue. ` : ""}
            Use <b>Mark as Paid</b> in Invoice History below to record the customer's payment. The credit line
            becomes active again automatically once no overdue invoice is left unpaid.
          </p>
        </div>
      )}

      {pendingTermUpdate && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm">
          <p className="font-semibold text-yellow-800 mb-1">Pending customer acceptance</p>
          <p className="text-yellow-800">
            New limit: INR {Number(pendingTermUpdate.new_credit_limit).toLocaleString("en-IN")}, new term: {pendingTermUpdate.new_payment_term_days} days.
            The current terms remain active until the customer accepts.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
      <div className="bg-white rounded-xl border p-6 lg:col-span-2">
        <h2 className="font-semibold text-gray-800 mb-3">Update Credit Limit / Payment Term</h2>
        <form onSubmit={submitTermUpdate} className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="w-full sm:flex-1">
            <label className="block text-xs text-gray-500 mb-1">New Credit Limit (INR)</label>
            <input
              type="number"
              value={newLimit}
              onChange={(e) => setNewLimit(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm w-full"
              disabled={!!pendingTermUpdate}
            />
          </div>
          <div className="w-full sm:flex-1">
            <label className="block text-xs text-gray-500 mb-1">New Payment Term</label>
            <PaymentTermSelect value={newTerm} onChange={setNewTerm} disabled={!!pendingTermUpdate} />
          </div>
          <button
            type="submit"
            disabled={requesting || !!pendingTermUpdate}
            className="rounded-lg bg-blue-600 text-white px-4 py-2 text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
          >
            {requesting ? "Sending…" : "Send to Customer"}
          </button>
        </form>
        <p className="text-xs text-gray-500 mt-2">
          A credit limit increase applies only after the customer accepts it. A decrease or a payment-term-only
          change applies immediately and the customer is simply notified. Existing invoices always keep their
          original terms.
        </p>
      </div>

      <div className="bg-white rounded-xl border p-6">
        <h2 className="font-semibold text-gray-800 mb-3">Admin Controls</h2>
        <button
          type="button"
          onClick={toggleStatus}
          className="w-full rounded-lg border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
        >
          {creditLine.status === "disabled" ? "Enable Credit Line" : "Disable Credit Line"}
        </button>
        <p className="text-xs text-gray-500 mt-2">
          {creditLine.status === "disabled"
            ? "Enabling restores the line; its status is then recalculated from overdue invoices."
            : "Disabling stops the customer from using this credit line until it is enabled again."}
        </p>
      </div>
      </div>

      <div className="bg-white rounded-xl border overflow-x-auto">
        <div className="px-6 py-3 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <span className="font-semibold text-gray-800">Invoice History</span>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <button
              type="button"
              onClick={() => handleInvoicePage(invoiceMeta.page - 1)}
              disabled={invoiceMeta.page <= 1 || invoicesLoading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
            >
              Prev
            </button>
            <span className="text-gray-700">
              Page <b>{invoiceMeta.page}</b> / <b>{invoiceMeta.totalPages}</b> (Total: <b>{invoiceMeta.total}</b>)
            </span>
            <button
              type="button"
              onClick={() => handleInvoicePage(invoiceMeta.page + 1)}
              disabled={invoiceMeta.page >= invoiceMeta.totalPages || invoicesLoading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-600 whitespace-nowrap">
            <tr>
              <th className="px-4 py-3">Booking</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Paid</th>
              <th className="px-4 py-3">Due Date</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">No invoices yet</td></tr>
            ) : (
              invoices.map((inv) => {
                const isOutstanding = inv.status === "unpaid" || inv.status === "partial";
                const isOverdue = isOutstanding && inv.due_date < todayIst();
                const isProcessing = inv.payment_state === "processing";
                return (
                  <tr key={inv.id} className={`border-t ${isOverdue ? "bg-red-50/40" : ""}`}>
                    <td className="px-4 py-3 break-all min-w-[12rem]">
                      {inv.booking_id}
                      {inv.invoice_number && <div className="text-xs text-gray-500">{inv.invoice_number}</div>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">INR {Number(inv.amount).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 whitespace-nowrap">INR {Number(inv.paid_amount).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {inv.due_date}
                      {isOverdue && <div className="text-xs font-semibold text-red-600">Overdue</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${INVOICE_BADGE[inv.status] || ""}`}>
                        {inv.status}
                      </span>
                      {inv.admin_remarks && (
                        <div className="text-xs text-gray-500 mt-1 max-w-[16rem] break-words" title={inv.admin_remarks}>
                          {inv.admin_remarks}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {isOutstanding ? (
                        <div className="inline-flex flex-col items-end gap-1">
                          <button
                            type="button"
                            onClick={() => openMarkPaid(inv)}
                            title={isProcessing ? "A customer payment is processing. You can still mark this invoice as paid." : undefined}
                            className="rounded-lg border border-blue-200 text-blue-600 px-3 py-1 text-xs font-semibold hover:bg-blue-600 hover:text-white hover:border-blue-600"
                          >
                            Mark as Paid
                          </button>
                          {isProcessing && (
                            <span className="text-[11px] font-medium text-amber-600">Customer payment processing</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {payingInvoice && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={closeMarkPaid}>
          <form
            onSubmit={submitMarkPaid}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
          >
            <div className="px-6 py-4 border-b">
              <h2 className="font-semibold text-gray-800">Mark Invoice as Paid</h2>
              <p className="text-xs text-gray-500 mt-1 break-all">
                {payingInvoice.invoice_number || payingInvoice.booking_id}
              </p>
            </div>

            <div className="px-6 py-4 space-y-4">
              {payingInvoice.payment_state === "processing" && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  A customer payment is processing for this invoice. Marking it as paid stops that payment from
                  being applied. If the customer's payment still goes through, it is not recorded and has to be
                  refunded to the customer.
                </div>
              )}
              <div className="rounded-lg bg-gray-50 p-4 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-500">Invoice Amount</span>
                  <span>{inr(payingInvoice.amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Already Paid</span>
                  <span>{inr(payingInvoice.paid_amount)}</span>
                </div>
                <div className="flex justify-between font-semibold text-gray-800 pt-1 border-t">
                  <span>Amount Received</span>
                  <span>{inr(outstandingOf(payingInvoice))}</span>
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">Payment Method</label>
                <select
                  value={payMethod}
                  onChange={(e) => setPayMethod(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm w-full"
                >
                  {OFFLINE_PAYMENT_METHODS.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Payment Reference <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  maxLength={100}
                  placeholder="UTR, transaction ID or receipt number"
                  value={payReference}
                  onChange={(e) => setPayReference(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm w-full"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">Payment Date</label>
                <input
                  type="date"
                  max={todayIst()}
                  value={payDate}
                  onChange={(e) => setPayDate(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm w-full"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Remarks <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  rows={3}
                  maxLength={500}
                  placeholder="Why is this invoice being marked as paid? Visible to admins only."
                  value={payRemarks}
                  onChange={(e) => setPayRemarks(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm w-full resize-none"
                />
                <div className="text-right text-[11px] text-gray-400">{payRemarks.length}/500</div>
              </div>

              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={payNotify}
                  onChange={(e) => setPayNotify(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  Send receipt to customer
                  <span className="block text-xs text-gray-500">
                    Push notification, payment receipt email and updated invoice. The receipt is saved in the
                    customer's payment history either way.
                  </span>
                </span>
              </label>

              <p className="text-xs text-gray-500">
                The full outstanding amount is recorded as received and added back to the available balance.
                This cannot be undone from the admin panel.
              </p>
            </div>

            <div className="px-6 py-4 border-t flex justify-end gap-2">
              <button
                type="button"
                onClick={closeMarkPaid}
                disabled={markingPaid}
                className="rounded-lg border px-4 py-2 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={markingPaid}
                className="rounded-lg bg-blue-600 text-white px-4 py-2 text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
              >
                {markingPaid ? "Saving…" : "Confirm Payment"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
